import {useCallback, useMemo} from 'react';
import {useViews} from '../use-views';
import type {IAdobeDataPush, stepCall, successCall, trackTypes} from './types';

export type formEvent = 'form_start' | 'form_engagement' | 'form_complete';
export type nlEvent = 'Newsletter_started' | 'newsletter_submit';

export const useTracking = <T extends trackTypes>(
  formName: T['formName'],
  formStepName: T['formStepName'],
): [stepCall<T>, successCall<T>] => {
  const {entryView} = useViews();

  const formEntry =
    entryView?.tokenRequired === `required` ? `email` : `process`;

  const dataPush = useMemo(() => {
    const adobeDataLayer = (window as any)?.adobeDataLayer;

    if (
      adobeDataLayer &&
      typeof adobeDataLayer === `object` &&
      typeof adobeDataLayer.push === `function`
    ) {
      return (obj: IAdobeDataPush<T>) => {
        adobeDataLayer.push(obj);
      };
    }

    return ({event, eventInfo: info}: IAdobeDataPush<T>) => {
      console.info(
        `%c[TRK] ${info.formName} ${info.formStep} ${info.formStepName} ${event} ${info.formEntry}`,
        `color: #0074D9`,
      );
    };
  }, []);

  const _step = useCallback(
    (
      formStep: number,
      event: T['event'],
      newFormStepName?: T['successStepName'],
    ) => {
      const obj: IAdobeDataPush<T> = {
        event,
        eventInfo: {
          id: Math.round(Math.random() * 100000000).toString(),
          formName,
          formStep: `step${formStep}`,
          formStepName: newFormStepName || formStepName,
          featureCategory: `form`,
          formEntry,
        },
      };

      dataPush(obj);
    },

    [formName, formStepName, formEntry],
  );

  const step: stepCall<T> = useCallback(
    (formStep: number, event: T['event']) => {
      _step(formStep, event);
    },

    [_step],
  );

  const success: successCall<T> = (formStep, stepName) => {
    _step(formStep, `form_complete`, stepName);
  };

  return [step, success];
};
